import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { API_ROUTES, API_DOMAIN, headers } from '../../../Reusable_Components/Config/apiConfig';
import { useParams, useNavigate } from 'react-router-dom';
import { useApiCache } from '../../../../hooks/useApiCache';
import { useListState } from '../../../../hooks/useListState';
import { useScrollRestoration } from '../../../../hooks/useScrollRestoration';
import * as listCache from '../../../../services/listCache';
import { ToastContext } from '../../../../context/ToastContext';
import ConfirmModal from '../../../Reusable_Components/ConfirmModal';
import { fmtDate } from '../../../../utils/fmt';

function pageRange(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, current, current - 1, current + 1].filter(p => p >= 1 && p <= total));
  return [...set].sort((a, b) => a - b);
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];


const OpportunityFollowUpListDetails = () => {
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [totalItems, setTotalItems] = useState(0);
  const [fetchVersion, setFetchVersion] = useState(0);
  const { opportunityId } = useParams();
  const navigate = useNavigate();
  const toast = useContext(ToastContext);

  // Retrieve cached, auto-unwrapped arrays for users and opportunities
  const { data: usersData } = useApiCache(`${API_DOMAIN}${API_ROUTES.users}`, headers);
  const { data: opportunitiesData } = useApiCache(`${API_DOMAIN}${API_ROUTES.opportunity}`, headers);
  const users = Array.isArray(usersData) ? usersData : [];
  const opportunities = Array.isArray(opportunitiesData) ? opportunitiesData : [];

  const [listState, setListState] = useListState({ search: '', page: 1, pageSize: 25 });
  const search = listState.search;
  const page = listState.page;
  const pageSize = listState.pageSize;
  const setSearch = (val) => setListState((s) => ({ ...s, search: val }));
  const setPage = (val) => setListState((s) => ({ ...s, page: val }));
  const changePageSize = (n) => setListState((s) => ({ ...s, pageSize: n, page: 1 }));

  const baseUrl = opportunityId
    ? `${API_DOMAIN}${API_ROUTES.opportunityFollowUp}${opportunityId}/`
    : `${API_DOMAIN}${API_ROUTES.opportunityFollowUp}`;
  const refetch = useCallback(() => setFetchVersion(v => v + 1), []);

  const restoreScroll = useScrollRestoration('/opportunity/follow-up/list');
  useEffect(() => { if (!loading) restoreScroll(); }, [loading, restoreScroll]);

  useEffect(() => {
    let cancelled = false;
    const url = `${baseUrl}?page=${page}&page_size=${pageSize}`;

    const applyData = (raw) => {
      // Unwrap standard_response envelope: { status, message, data: { results, count } }
      const payload = (raw?.data !== undefined && typeof raw.data === 'object') ? raw.data : raw;
      const items = Array.isArray(payload) ? payload : (Array.isArray(payload?.results) ? payload.results : []);
      setFollowUps(items);
      setTotalItems(Array.isArray(payload) ? items.length : (payload?.count ?? items.length));
    };

    // Stale-while-revalidate: instant render from cache, background refresh only if stale.
    const cached = listCache.get(url);
    if (cached) {
      applyData(cached.data);
      setLoading(false);
    } else {
      setLoading(true);
    }

    if (!cached || cached.isStale) {
      listCache
        .fetchDeduped(url, headers)
        .then(data => {
          if (cancelled) return;
          listCache.set(url, data);
          applyData(data);
        })
        .catch(() => {
          if (cancelled) return;
          if (!cached) toast?.addToast({ type: 'error', message: 'Failed to load follow-ups.' });
        })
        .finally(() => { if (!cancelled) setLoading(false); });
    }

    return () => { cancelled = true };
  }, [baseUrl, page, pageSize, fetchVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_DOMAIN}${API_ROUTES.opportunityFollowUp}${id}/`, { headers });
      toast?.addToast({ type: 'success', message: 'Follow-up deleted.' });
      listCache.invalidate((u) => u.startsWith(baseUrl.split('?')[0]));
      refetch();
    } catch {
      toast?.addToast({ type: 'error', message: 'Failed to delete follow-up.' });
    }
  };

  // Client-side search filters within the current page
  const q = search.toLowerCase();
  const filtered = q
    ? followUps.filter(f =>
        (f.description || '').toLowerCase().includes(q) ||
        (users.find(u => u.id === f.assigned_user)?.username || '').toLowerCase().includes(q)
      )
    : followUps;

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const pages = pageRange(page, totalPages);
  const showFrom = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const showTo = Math.min(page * pageSize, totalItems);
  const goTo = (p) => setPage(Math.max(1, Math.min(p, totalPages)));

  return (
    <div>
      <div className="card">
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 340 }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }}>🔍</span>
            <input
              className="form-input"
              style={{ paddingLeft: 32 }}
              placeholder="Search by description, assigned user…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
            {loading
              ? 'Loading…'
              : q
                ? `${filtered.length.toLocaleString()} match${filtered.length !== 1 ? 'es' : ''} on this page`
                : `${totalItems.toLocaleString()} follow-up${totalItems !== 1 ? 's' : ''}`}
          </span>
        </div>

        <div className="table-scroll">
          <table className="crm-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Due Date</th>
                <th>Assigned User</th>
                <th>Opportunity</th>
                <th style={{ textAlign: 'right', width: 1 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i}>
                      {[60, 45, 50, 55, 80].map((w, j) => (
                        <td key={j}><div className="skeleton" style={{ height: 13, width: `${w}%`, borderRadius: 4 }} /></td>
                      ))}
                    </tr>
                  ))
                : filtered.length === 0
                  ? <tr><td colSpan={5}>
                      <div className="empty-state">
                        <div className="empty-state-icon-wrap">
                          {search ? (
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" />
                            </svg>
                          ) : (
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 8v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8" />
                              <path d="M3 8l2.5-4.5A1 1 0 0 1 6.37 3h11.26a1 1 0 0 1 .87.5L21 8" />
                              <path d="M3 8h18" /><path d="M9 12h6" />
                            </svg>
                          )}
                        </div>
                        <p className="empty-state-title">{search ? 'No matching records' : 'No follow-ups yet'}</p>
                        <p className="empty-state-text">
                          {search ? `We couldn't find anything for "${search}" on this page.` : 'Click "New Follow-Up" to create your first one.'}
                        </p>
                        {search && (
                          <button className="btn btn-secondary btn-sm" style={{ marginTop: 16 }} onClick={() => setSearch('')}>
                            Clear search
                          </button>
                        )}
                      </div>
                    </td></tr>
                  : filtered.map(followUp => (
                    <tr key={followUp.id}>
                      <td style={{ fontSize: 13, color: 'var(--text-main)' }}>
                        {followUp.description || '—'}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {fmtDate(followUp.due_date)}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-main)' }}>
                        {users.find(u => u.id === followUp.assigned_user)?.username || 'Unknown User'}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-main)' }}>
                        {opportunities.find(o => o.id === followUp.opportunity)?.opportunity_name || 'Unknown Opportunity'}
                      </td>
                      <td>
                        <div className="table-actions">
                          <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/opportunity/follow-up/details/${btoa(followUp.id)}`)}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{flexShrink:0}}><path d="M1 8s3-5 7-5 7 5 7 5-3 5-7 5-7-5-7-5z"/><circle cx="8" cy="8" r="2"/></svg>
                            View
                          </button>
                          <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/opportunity/follow-up/input/${btoa(followUp.id)}`)}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{flexShrink:0}}><path d="M11.5 2.5l2 2L5 13H3v-2L11.5 2.5z"/></svg>
                            Edit
                          </button>
                          <button className="btn btn-danger btn-sm" onClick={() => setPendingDelete(followUp.id)}>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{flexShrink:0}}><path d="M2 4h12M6 4V3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1M5 4v9a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1V4"/></svg>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
              }
            </tbody>
          </table>
        </div>

        {!loading && totalItems > 0 && (
          <div className="pagination-wrap">
            <span className="pagination-info">
              {showFrom.toLocaleString()}–{showTo.toLocaleString()} of {totalItems.toLocaleString()}
            </span>

            <div className="pagination-buttons">
              <button className="btn btn-secondary btn-sm" onClick={() => goTo(1)} disabled={page === 1} title="First">«</button>
              <button className="btn btn-secondary btn-sm" onClick={() => goTo(page - 1)} disabled={page === 1} title="Previous">‹</button>
              {pages.map((p, idx) => {
                const prev = pages[idx - 1];
                return (
                  <React.Fragment key={p}>
                    {prev && p - prev > 1 && <span className="pagination-ellipsis">…</span>}
                    <button
                      className={`btn btn-sm pagination-page-btn${p === page ? ' active' : ''}`}
                      onClick={() => goTo(p)}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                );
              })}
              <button className="btn btn-secondary btn-sm" onClick={() => goTo(page + 1)} disabled={page === totalPages} title="Next">›</button>
              <button className="btn btn-secondary btn-sm" onClick={() => goTo(totalPages)} disabled={page === totalPages} title="Last">»</button>
            </div>

            <div className="pagination-size-wrap">
              <label className="pagination-size-label">Rows:</label>
              <select
                className="pagination-size-select"
                value={pageSize}
                onChange={e => changePageSize(Number(e.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        open={pendingDelete !== null}
        title="Delete Follow-Up"
        message="Are you sure you want to delete this follow-up? This cannot be undone."
        confirmLabel="Delete"
        confirmDanger
        onConfirm={() => { handleDelete(pendingDelete); setPendingDelete(null); }}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default OpportunityFollowUpListDetails;
